/**
 * DOOH Page – the standalone DOOH service inside the dashboard, so the sidebar
 * stays at hand. DOOH is served by the API under /dooh/ and checks the same
 * session cookie itself. It follows the app's language (?lang=en|ru); switching
 * the language reloads it.
 */
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Navigate } from "react-router-dom";
import { useDoohAccess } from "@/hooks/use-dooh-access";
import { useAccess } from "@/hooks/use-access";
import { doohUrl } from "@/lib/dooh-url";

export function DoohPage() {
	const { user } = useAuth();
	const { allowed, isLoading, url } = useDoohAccess(user?.id);
	const { homePath } = useAccess();
	const { i18n } = useTranslation();

	// Decide access once. DOOH re-checks the session on every request, and a
	// failed background re-check (e.g. after the laptop wakes) should not throw
	// the user out mid-work.
	const access = useRef<boolean | null>(null);
	if (access.current === null && user && !isLoading) access.current = allowed;

	if (access.current === null) {
		return (
			<div className="flex flex-1 items-center justify-center">
				<Loader2 size={24} className="animate-spin text-text-tertiary" />
			</div>
		);
	}

	// The DOOH service decides on its own too. If it says no, go to the main
	// page, or to Settings when DOOH itself is the main page (no loop).
	if (!access.current) {
		return <Navigate to={homePath === "/out-of-home" ? "/settings" : homePath} replace />;
	}

	return <iframe src={doohUrl(url, i18n.resolvedLanguage ?? i18n.language)} title="DOOH" className="w-full flex-1 border-0" />;
}
